import { useCallback, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ── Types ────────────────────────────────────────────────────────

export interface PageTab {
  pageId: string;
  pageName: string;
  isDefault: boolean;
  position: number;
}

interface PageTabBarProps {
  pages: PageTab[];
  activePageId: string;
  onSelectPage: (pageId: string) => void;
  onRenamePage: (pageId: string, newName: string) => void;
  onDeletePage: (pageId: string) => void;
  onReorderPages: (pages: PageTab[]) => void;
  onCreatePage: () => void;
}

// ── SortableTab ──────────────────────────────────────────────────

function SortableTab({
  tab,
  isActive,
  onSelect,
  onRename,
  onDelete,
  canDelete,
}: {
  tab: PageTab;
  isActive: boolean;
  onSelect: () => void;
  onRename: (newName: string) => void;
  onDelete: () => void;
  canDelete: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(tab.pageName);
  const [showMenu, setShowMenu] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.pageId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const commitRename = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== tab.pageName) {
      onRename(trimmed);
    } else {
      setEditValue(tab.pageName);
    }
    setEditing(false);
  }, [editValue, tab.pageName, onRename]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(true);
    setEditValue(tab.pageName);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitRename();
    } else if (e.key === 'Escape') {
      setEditValue(tab.pageName);
      setEditing(false);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowMenu(true);
    const closeMenu = (ev: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(ev.target as Node)) {
        setShowMenu(false);
        document.removeEventListener('click', closeMenu);
      }
    };
    setTimeout(() => document.addEventListener('click', closeMenu), 0);
  };

  const handleDelete = () => {
    setShowMenu(false);
    if (window.confirm(`Delete page "${tab.pageName}"? This cannot be undone.`)) {
      onDelete();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sp-page-tab${isActive ? ' sp-page-tab--active' : ''}`}
      onClick={onSelect}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      {...attributes}
      {...listeners}
    >
      {editing ? (
        <input
          ref={inputRef}
          className="sp-page-tab-input"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          autoFocus
        />
      ) : (
        <span className="sp-page-tab-label">{tab.pageName}</span>
      )}
      {tab.isDefault && <span className="sp-page-tab-badge" title="Default page">*</span>}

      {showMenu && (
        <div ref={menuRef} className="sp-page-tab-menu">
          <button
            className="sp-page-tab-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(false);
              setEditing(true);
              setEditValue(tab.pageName);
              setTimeout(() => inputRef.current?.select(), 0);
            }}
          >
            Rename
          </button>
          {canDelete && (
            <button
              className="sp-page-tab-menu-item sp-page-tab-menu-item--danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── PageTabBar ───────────────────────────────────────────────────

export function PageTabBar({
  pages,
  activePageId,
  onSelectPage,
  onRenamePage,
  onDeletePage,
  onReorderPages,
  onCreatePage,
}: PageTabBarProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const sorted = [...pages].sort((a, b) => a.position - b.position);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sorted.findIndex((p) => p.pageId === active.id);
    const newIndex = sorted.findIndex((p) => p.pageId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sorted, oldIndex, newIndex).map((p, i) => ({
      ...p,
      position: i,
    }));
    onReorderPages(reordered);
  };

  return (
    <div className="sp-page-tab-bar">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sorted.map((p) => p.pageId)} strategy={horizontalListSortingStrategy}>
          {sorted.map((tab) => (
            <SortableTab
              key={tab.pageId}
              tab={tab}
              isActive={tab.pageId === activePageId}
              onSelect={() => onSelectPage(tab.pageId)}
              onRename={(name) => onRenamePage(tab.pageId, name)}
              onDelete={() => onDeletePage(tab.pageId)}
              canDelete={pages.length > 1}
            />
          ))}
        </SortableContext>
      </DndContext>
      <button
        className="sp-page-tab-add"
        onClick={onCreatePage}
        title="Add new page"
      >
        +
      </button>
    </div>
  );
}
